import React, { useState } from 'react';
import { Image } from 'react-bootstrap-icons';

import { formatMoney, formatNomenclatureName, formatProductionName } from '../../utils/gazTextFormat.js';
import {
  GAZ_SORT_ASC,
  GAZ_SORT_BY_PRICE_PER_M3,
  GAZ_SORT_BY_TOTAL,
  GAZ_SORT_DESC,
  isInsideInteractiveElement,
  nextGazSortDirection,
  shouldActivateComparisonRow,
  sortGazComparisonRows,
} from './gazComparisonView.js';
import GazTotalTooltip from './GazTotalTooltip';
import GazDeliveryTooltip from './GazDeliveryTooltip';

import './gazComparisonTable.css';

/**
 * Таблица сравнения производств — МСК Газобетон.
 *
 * Дополнительный инструмент проверки, а не второй расчёт: основной расчёт заказа
 * стоит выше на экране и остаётся главным. Поэтому здесь нет ни карточек
 * «минимальная стоимость» и «выбрано менеджером» над таблицей, ни свёрнутого
 * состояния — весь список видно сразу.
 *
 * Своих сумм не считает: строки приходят готовыми из gazComparisonView, порядок —
 * из существующей сортировки калькулятора. Производство выбирается нажатием на всю
 * строку: отдельная кнопка в каждой строке повторяла то, на что менеджер и так
 * целится, и занимала колонку.
 *
 * Единственный собственный порядок — необязательная сортировка по цене за куб: она
 * только переставляет готовые строки и не трогает ни суммы, ни выбор, ни метку
 * минимальной цены.
 */

const DIFFERENCE_LABELS = {
  min: 'Минимальная',
  same: 'Та же цена',
};

const differenceTextOf = (difference) => {
  if (!difference || difference.kind === 'none') {
    return '—';
  }
  if (difference.kind === 'more') {
    return `+${formatMoney(difference.amount)} ₽`;
  }
  // Дешевле минимальной бывает только строка второго сорта — это другой товар,
  // поэтому экономия называется числом, а не меткой «минимальная».
  if (difference.kind === 'less') {
    return `−${formatMoney(difference.amount)} ₽`;
  }
  return DIFFERENCE_LABELS[difference.kind] || '—';
};

const GazComparisonTable = ({
  view,
  selfPickUp = false,
  hasUpsells = false,
  onSelect,
  // Фото приходит готовым из таблицы-владельца: она уже спрашивает сервер по всем
  // строкам сразу и держит модалку. Здесь — только кнопка.
  hasPhoto = () => false,
  onOpenPhoto = () => {},
  onProductionMouseEnter,
  onProductionMouseLeave,
}) => {
  // Сортировка — состояние самой таблицы: расчёт, автовыбор и порядок
  // калькулятора о ней не знают. Сортируется одна колонка за раз.
  const [sort, setSort] = useState({ key: null, direction: null });

  const viewRows = Array.isArray(view?.rows) ? view.rows : [];
  const rows = sortGazComparisonRows({ rows: viewRows, key: sort.key, direction: sort.direction });

  if (rows.length === 0) {
    return null;
  }

  // Нажатие по другой колонке начинает с возрастания, по той же — переключает
  // направление и третьим нажатием возвращает порядок калькулятора.
  const toggleSort = (key) => setSort((current) => {
    const direction = nextGazSortDirection(current.key === key ? current.direction : null);
    return { key: direction ? key : null, direction };
  });

  const directionOf = (key) => (sort.key === key ? sort.direction : null);

  const ariaSortOf = (key) => {
    const direction = directionOf(key);
    if (direction === GAZ_SORT_ASC) {
      return 'ascending';
    }
    return direction === GAZ_SORT_DESC ? 'descending' : 'none';
  };

  const arrowOf = (key) => {
    const direction = directionOf(key);
    if (direction === GAZ_SORT_ASC) {
      return '↑';
    }
    return direction === GAZ_SORT_DESC ? '↓' : '⇅';
  };

  // Уточнение к подписи — знаком вопроса рядом, а не длинным заголовком: полное
  // название столбца в шапку из восьми колонок не помещается. Знак стоит снаружи
  // кнопки сортировки, поэтому наведение объясняет, а не сортирует.
  const renderSortableHeader = (key, label, title, hint) => (
    <th
      scope="col"
      className="gaz-cmp__th gaz-cmp__th--sortable"
      aria-sort={ariaSortOf(key)}
    >
      <button
        type="button"
        className={`gaz-cmp__sort${directionOf(key) ? ' is-active' : ''}`}
        onClick={() => toggleSort(key)}
        title={title}
      >
        <span className="gaz-cmp__sort-label">{label}</span>
        <span className="gaz-cmp__sort-arrow" aria-hidden="true">{arrowOf(key)}</span>
      </button>
      {hint && (
        <span className="gaz-cmp__hint" title={hint} tabIndex={0} role="note" aria-label={hint}>
          ?
        </span>
      )}
    </th>
  );

  // Нажатие по строке и Enter/Space — один и тот же путь: решение принимает
  // shouldActivateComparisonRow, компонент только сообщает о выборе наверх.
  const activateRow = (event, row, key) => {
    const allowed = shouldActivateComparisonRow({
      key,
      isSelected: row.isSelected,
      insideInteractive: isInsideInteractiveElement(event),
    });
    if (!allowed) {
      return;
    }
    // Пробел на строке иначе прокрутит страницу.
    if (key !== undefined) {
      event.preventDefault();
    }
    if (onSelect) {
      onSelect(row.comparison, row.entry);
    }
  };

  // Подпись колонки короткая; на узком экране карточка называет форму оплаты
  // целиком, потому что там колонка не подписана шапкой.
  const payLabel = view?.isCashRounded ? 'К оплате наличными' : 'К оплате';

  return (
    <div className="gaz-cmp">
      <p className="gaz-cmp__lead">
        Выберите другое производство, если оно лучше подходит по цене или расположению
      </p>

      {/* Сравнение считается только по строкам «Товаров»: допродажи в него не
          попадают (BackendProductions строит сравнение по productsForCalc).
          Поэтому когда допродажи в заказе есть, разница честно оговаривается —
          называть её экономией по всему заказу нельзя. */}
      {hasUpsells && (
        <p className="gaz-cmp__note">Сравнение газобетона и доставки без допродаж</p>
      )}

      {/* Источник у всех строк один — называем его один раз, а не десять. Если
          источники разные, здесь ничего нет: они остаются у своих строк. */}
      {view?.commonSource && (
        <p className="gaz-cmp__source">Источник цен: {view.commonSource}</p>
      )}

      <table className={`gaz-cmp__table${selfPickUp ? ' gaz-cmp__table--no-delivery' : ''}`}>
        <thead>
          <tr>
            <th scope="col" className="gaz-cmp__th gaz-cmp__th--num">№</th>
            <th scope="col" className="gaz-cmp__th gaz-cmp__th--text">Производство</th>
            <th scope="col" className="gaz-cmp__th">Материалы</th>
            {!selfPickUp && <th scope="col" className="gaz-cmp__th">Доставка</th>}
            {/* Сортируемые заголовки. aria-sort сообщает состояние скринридеру,
                стрелка — глазу; в нейтральном состоянии стоит бледный значок,
                иначе про сортировку невозможно догадаться. */}
            {renderSortableHeader(GAZ_SORT_BY_TOTAL, 'К оплате', 'Сортировать по общей сумме')}

            {renderSortableHeader(
              GAZ_SORT_BY_PRICE_PER_M3,
              'Цена за м³ с доставкой',
              'Сортировать по цене за кубометр',
              'при погрузке полной шаланды'
            )}

            <th scope="col" className="gaz-cmp__th">Разница</th>
            <th scope="col" className="gaz-cmp__th">Цена обновлена</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id}
              className={`gaz-cmp__row${row.isSelected ? ' is-selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={row.isSelected}
              aria-label={`Выбрать производство ${row.production}`}
              onClick={(event) => activateRow(event, row)}
              onKeyDown={(event) => activateRow(event, row, event.key)}
            >
              <td className="gaz-cmp__cell gaz-cmp__cell--num">{index + 1}</td>

              <td
                className="gaz-cmp__cell gaz-cmp__cell--prod"
                onMouseEnter={onProductionMouseEnter ? (event) => onProductionMouseEnter(event, row) : undefined}
                onMouseLeave={onProductionMouseLeave}
              >
                {/* Минимальная цена и выбранный вариант — независимые состояния:
                    выбранное производство не обязано быть самым дешёвым, и обе
                    метки могут оказаться в одной строке. */}
                {(row.isBest || row.isSelected || row.isVtoroySort) && (
                  <span className="gaz-cmp__flags">
                    {row.isBest && (
                      <span className="gaz-cmp__flag gaz-cmp__flag--best">↓ Минимальная цена</span>
                    )}
                    {row.isSelected && (
                      <span className="gaz-cmp__flag gaz-cmp__flag--picked">✓ Выбрано</span>
                    )}
                    {/* Второй сорт стоит ниже обычных блоков и внешне от них не
                        отличается — пометка обязательна, иначе менеджер продаст
                        его как обычный блок подешевле. */}
                    {row.isVtoroySort && (
                      <span
                        className="gaz-cmp__flag gaz-cmp__flag--vtoroy-sort"
                        title="Плотности в партии вперемешку, возможны сколы. Товар невозвратный, оплата 100%."
                      >
                        2 сорт
                      </span>
                    )}
                  </span>
                )}
                <span className="gaz-cmp__title">
                  {/* Фото этого завода. Кнопка есть только там, где фото
                      действительно заведено: пустая кнопка в каждой строке
                      означала бы «смотреть нечего» восемь раз подряд. Нажатие
                      производство не выбирает — кнопка попадает под
                      isInsideInteractiveElement. */}
                  {hasPhoto(row) && (
                    <button
                      type="button"
                      className="gaz-cmp__photo"
                      title="Показать фото"
                      aria-label={`Показать фото: ${row.production}`}
                      onClick={(event) => onOpenPhoto(row, event)}
                    >
                      <Image size={12} />
                    </button>
                  )}
                  <span className="gaz-cmp__production">{formatProductionName(row.production)}</span>
                </span>
                {row.nomenclature.length > 0 && (
                  <span className="gaz-cmp__nomenclature">
                    {row.nomenclature.map(formatNomenclatureName).join(' · ')}
                  </span>
                )}
                {row.rowSources.length > 0 && (
                  <span className="gaz-cmp__row-source">{row.rowSources.join(', ')}</span>
                )}
                {row.hasDeliveryError && (
                  <span className="gaz-cmp__error">
                    {row.deliveryError || 'Не удалось рассчитать доставку'}
                  </span>
                )}
              </td>

              <td className="gaz-cmp__cell gaz-cmp__cell--money" data-label="Материалы">
                {row.materialsTotal > 0 ? `${formatMoney(row.materialsTotal)} ₽` : '—'}
              </td>

              {!selfPickUp && (
                <td className="gaz-cmp__cell gaz-cmp__cell--money" data-label="Доставка">
                  {row.hasDeliveryError
                    ? 'Ошибка расчёта'
                    : (
                      // Километраж плеча при наведении. Если расстояния нет,
                      // подсказка сама отдаёт сумму обычным текстом.
                      <GazDeliveryTooltip entry={row.entry}>
                        {row.deliveryTotal > 0 ? `${formatMoney(row.deliveryTotal)} ₽` : '—'}
                      </GazDeliveryTooltip>
                    )}
                </td>
              )}

              {/* Сумма раскрывает расшифровку: наведение и фокус на широком
                  экране, нажатие — на телефоне. Выбор производства при этом не
                  переключается. */}
              <td
                className={`gaz-cmp__cell gaz-cmp__cell--total${row.isBest ? ' is-best' : ''}`}
                data-label={payLabel}
              >
                {row.payableTotal !== null ? (
                  <GazTotalTooltip entry={row.entry} payableTotal={row.payableTotal}>
                    {`${formatMoney(row.payableTotal)} ₽`}
                  </GazTotalTooltip>
                ) : 'Нет данных'}
              </td>

              {/* Цена за куб с доставкой: сумма к оплате этой же строки, делённая
                  на её объём. Прочерк, когда объёма нет или доставка не посчиталась —
                  ноль читался бы как «бесплатно». */}
              <td className="gaz-cmp__cell gaz-cmp__cell--perm3" data-label="Цена за м³ с доставкой">
                {row.pricePerM3 !== null && row.pricePerM3 !== undefined
                  ? `${formatMoney(row.pricePerM3)} ₽/м³`
                  : '—'}
              </td>

              <td className="gaz-cmp__cell gaz-cmp__cell--diff" data-label="Разница">
                <span className={`gaz-cmp__diff gaz-cmp__diff--${row.difference.kind}`}>
                  {differenceTextOf(row.difference)}
                </span>
              </td>

              <td className="gaz-cmp__cell gaz-cmp__cell--fresh" data-label="Цена обновлена">
                {row.priceFreshness || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GazComparisonTable;
